import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import UserBookmarkConstants from "constants/user-bookmark-constants";
import UserBookmark from "models/interfaces/user-bookmark";
import UserBookmarkRecord from "models/view-models/user-bookmark-record";
import RichTextEditorDeprecated from "molecules/rich-text/rich-text-editor-deprecated";
import React, { useState } from "react";
import useUpdateBookmark from "utilities/hooks/domain/my-link/use-update-bookmark";
import { t } from "utilities/localization-utils";
import StringUtils from "utilities/string-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import { ObjectValidationResult } from "utilities/validation/object-validator/object-validator";
import MyBookmarkMiniColorSelect from "./my-bookmark-mini-color-select";
import { UserBookmarkColors } from "utilities/enumerations/user-bookmark-colors";

interface MyBookmarkMetaProps {
    bookmark: UserBookmarkRecord;
    onMetaChange: any;
    isAllowedToEdit: boolean;
}

interface NoteText {
    rawValue: string;
    actualValue: string;
}

const CSS_CLASS_NAME = "c-my-bookmark-meta";

const MyBookmarkMeta = (props: MyBookmarkMetaProps) => {
    const { bookmark, onMetaChange, isAllowedToEdit } = props;
    const [isEditingMeta, setIsEditingMeta] = useState(false);
    const [updatedNote, setUpdatedNote] = useState<NoteText>();
    const [updatedColor, setUpdatedColor] = useState<UserBookmarkColors>();

    const [validationMessages, setValidationMessages] = useState<
        ObjectValidationResult<UserBookmark>
    >({});

    const reset = () => {
        setUpdatedColor(undefined);
        setUpdatedNote(undefined);
        setIsEditingMeta(false);
        setValidationMessages({});
    };

    const handleSuccess = () => {
        reset();
        ToastManager.success(t("updatedItem", { item: t("bookmark") }));
    };

    const handleError = () => {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("updating"),
                resource: t("bookmark"),
            })
        );
    };
    const { updateBookmark } = useUpdateBookmark(handleSuccess, handleError);

    const handleEditToggle = () => setIsEditingMeta(!isEditingMeta);
    const handleEditCancel = () => reset();
    const handleDescriptionChanged = (
        actualValue: string,
        rawValue: string
    ) => {
        setUpdatedNote({ actualValue, rawValue });
    };
    const handleOnSaveClick = async () => {
        if (
            StringUtils.hasValue(updatedNote?.rawValue) &&
            updatedNote.rawValue.length > UserBookmarkConstants.MAX_LENGTH
        ) {
            setValidationMessages({ description: [t("noteTextTooLong")] });
            return;
        }
        const updatedMeta: Partial<UserBookmark> = {
            ...(updatedColor && { color: updatedColor }),
            ...(updatedNote && {
                description: updatedNote?.actualValue,
                descriptionAsPlainText: updatedNote?.rawValue,
            }),
        };
        await updateBookmark(bookmark.with({ ...updatedMeta }));
        await onMetaChange();
        reset();
    };

    const handleUserBookmarkColorChanged = async (newColor: number) => {
        setUpdatedColor(newColor);
    };

    return (
        <div className={`${CSS_CLASS_NAME}`}>
            <div className={`${CSS_CLASS_NAME}__bookmarkColor`}>
                <MyBookmarkMiniColorSelect
                    isEditing={isEditingMeta}
                    color={updatedColor ?? bookmark.color}
                    onColorChange={handleUserBookmarkColorChanged}
                />
            </div>
            <div className={`${CSS_CLASS_NAME}__noteWrapper`}>
                <div className={`${CSS_CLASS_NAME}__noteHeader`}>
                    <Heading
                        cssClassName={`${CSS_CLASS_NAME}__noteHeading`}
                        priority={HeadingPriority.Four}>
                        {t("notes")}
                    </Heading>
                    {isEditingMeta && (
                        <>
                            <Button
                                dataTestId="bookmark-settings--meta--cancel-button"
                                size={ButtonSizes.Small}
                                style={ButtonStyles.Destructive}
                                onClick={handleEditCancel}
                                cssClassName={`${CSS_CLASS_NAME}__cancelButton`}>
                                {t("cancel")}
                            </Button>
                            <Button
                                dataTestId="bookmark-settings--meta--save-button"
                                size={ButtonSizes.Small}
                                style={ButtonStyles.Secondary}
                                onClick={handleOnSaveClick}
                                cssClassName={`${CSS_CLASS_NAME}__saveButton`}>
                                <Icon type={Icons.Checkmark} />
                                {t("saveChanges")}
                            </Button>
                        </>
                    )}
                    {isAllowedToEdit && !isEditingMeta && (
                        <Button
                            dataTestId="bookmark-settings--meta--edit-button"
                            onClick={handleEditToggle}
                            size={ButtonSizes.XSmall}
                            style={ButtonStyles.None}
                            cssClassName={`${CSS_CLASS_NAME}__editNoteButton`}
                            accessibleText={t("editItem", {
                                item: t("notes"),
                            })}>
                            <Icon type={Icons.Edit} />
                        </Button>
                    )}
                </div>
                {isEditingMeta ? (
                    <>
                        <div className={`${CSS_CLASS_NAME}__editor`}>
                            <RichTextEditorDeprecated
                                dataTestId="bookmark-settings--meta--notes-input"
                                errors={validationMessages.description}
                                label={
                                    <span className="sr-only">
                                        {t("field-notes")}
                                    </span>
                                }
                                onChange={handleDescriptionChanged}
                                value={bookmark.description}
                                maxLength={UserBookmarkConstants.MAX_LENGTH}
                                placeholder={t("addNoteHere")}
                                toolbarConfig={
                                    UserBookmarkConstants.RICH_TEXT_TOOLBAR_CONFIG
                                }
                            />
                        </div>
                    </>
                ) : (
                    <div
                        data-test-id="bookmark-settings--meta--note"
                        className={`${CSS_CLASS_NAME}__note`}
                        dangerouslySetInnerHTML={{
                            __html: StringUtils.stripHtmlTags(
                                bookmark?.description
                            )
                                ? bookmark?.description
                                : t("addNoteHere"),
                        }}
                    />
                )}
                <div className={`${CSS_CLASS_NAME}__meta`}>
                    <Paragraph
                        cssClassName={`${CSS_CLASS_NAME}__publicationSubtitle`}>
                        {bookmark?.getDisplayCodeAndEdition()}
                    </Paragraph>
                    <Paragraph cssClassName={`${CSS_CLASS_NAME}__addedOn`}>
                        {bookmark?.getModifiedDateFromNowText()}
                    </Paragraph>
                </div>
            </div>
        </div>
    );
};

export default MyBookmarkMeta;
